import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, RefreshCw, Info, CheckCircle, XCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  TanStackDataTable,
  userTableConfig,
  UserAdvancedFilters,
} from "@b2b/ui-components";
import { useUserList } from "../../hooks/useUserList";
import { useUserActions } from "../../hooks/useUserActions";
import { PendingUser, UserFilters } from "../../types/user.types";
import { createUserColumns } from "./UserList.columns";

const UserSearch = () => {
  console.log("🔍 UserSearch component rendered");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<UserFilters>({
    role: undefined,
    companyId: undefined,
    country: undefined,
    isActive: undefined,
    approvalStatus: undefined,
    startDate: undefined,
    endDate: undefined,
  });
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  const { users, loading, error, pagination, setPagination, refresh } =
    useUserList({
      searchTerm,
      advancedFilters,
      autoFetch: false,
    });

  const {
    loading: actionsLoading,
    approveUser,
    rejectUser,
    activateUser,
    suspendUser,
    deleteUser,
  } = useUserActions({
    onSuccess: () => refresh(),
  });

  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);

  const showToast = (type: "success" | "error" | "info", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  // Build filters
  useEffect(() => {
    const filters: string[] = [];
    if (searchTerm) filters.push(`Search: "${searchTerm}"`);
    if (advancedFilters.role) filters.push(`Role: ${advancedFilters.role}`);
    if (advancedFilters.companyId)
      filters.push(`Company: ${advancedFilters.companyId}`);
    if (advancedFilters.country)
      filters.push(`Country: ${advancedFilters.country}`);
    if (advancedFilters.isActive !== undefined)
      filters.push(
        `Status: ${advancedFilters.isActive ? "Active" : "Inactive"}`
      );
    if (advancedFilters.approvalStatus)
      filters.push(`Approval: ${advancedFilters.approvalStatus}`);
    setActiveFilters(filters);
  }, [searchTerm, advancedFilters]);

  // Perform search
  const handleSearch = async () => {
    await refresh();
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAdvancedFilters({
      role: undefined,
      companyId: undefined,
      country: undefined,
      isActive: undefined,
      approvalStatus: undefined,
      startDate: undefined,
      endDate: undefined,
    });
  };

  const handleRemoveFilter = (filter: string) => {
    if (filter.startsWith("Search:")) {
      setSearchTerm("");
    } else if (filter.startsWith("Role:")) {
      setAdvancedFilters((prev) => ({ ...prev, role: undefined }));
    } else if (filter.startsWith("Company:")) {
      setAdvancedFilters((prev) => ({ ...prev, companyId: undefined }));
    } else if (filter.startsWith("Country:")) {
      setAdvancedFilters((prev) => ({ ...prev, country: undefined }));
    } else if (filter.startsWith("Status:")) {
      setAdvancedFilters((prev) => ({ ...prev, isActive: undefined }));
    } else if (filter.startsWith("Approval:")) {
      setAdvancedFilters((prev) => ({ ...prev, approvalStatus: undefined }));
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleViewProfile = (userId: string) => {
    navigate(`/admin/userprofile/${userId}`);
  };

  const handleEditUser = (user: PendingUser) => {
    // Keep edit flow inside admin context and load role-specific form from selected user
    navigate(`/admin/profile_settings?userId=${user.id}&edit=true`);
  };

  const handleApproveUser = async (userId: string, user: PendingUser) => {
    const success = await approveUser(userId, user);
    if (success) {
      showToast("success", "User approved successfully");
    } else {
      showToast("error", "Failed to approve user");
    }
  };

  const handleRejectUser = async (userId: string, user: PendingUser) => {
    const success = await rejectUser(userId, user);
    if (success) {
      showToast("success", "User rejected successfully");
    } else {
      showToast("error", "Failed to reject user");
    }
  };

  const handleActivateUser = async (userId: string) => {
    const success = await activateUser(userId);
    if (success) {
      showToast("success", "User activated successfully");
    } else {
      showToast("error", "Failed to activate user");
    }
  };

  const handleSuspendUser = async (userId: string) => {
    const success = await suspendUser(userId);
    if (success) {
      showToast("success", "User suspended successfully");
    } else {
      showToast("error", "Failed to suspend user");
    }
  };

  const handleDeleteUser = async (userId: string) => {
    const success = await deleteUser(userId);
    if (success) {
      showToast("success", "User deleted successfully");
    } else {
      showToast("error", "Failed to delete user");
    }
  };

  // Create column definitions with memoization
  const columns = useMemo(
    () =>
      createUserColumns({
        loading,
        actionsLoading: actionsLoading ?? false,
        onViewProfile: handleViewProfile,
        onEditUser: handleEditUser,
        onApproveUser: handleApproveUser,
        onRejectUser: handleRejectUser,
        onActivateUser: handleActivateUser,
        onSuspendUser: handleSuspendUser,
        onDeleteUser: handleDeleteUser,
        showSelectColumn: false,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, actionsLoading]
  );

  return (
    <div className="space-y-8 mt-5">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-[9999] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : toast.type === "error"
                ? "bg-red-500 text-white"
                : "bg-blue-500 text-white"
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : toast.type === "error" ? (
              <XCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <Info className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="flex-1 font-medium text-sm">{toast.message}</p>
            <button
              onClick={() => setToast(null)}
              className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
              aria-label="Close notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Search Users</h1>
          <p className="text-muted-foreground">
            Search and filter users by name, email, role, status, or company
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleClearSearch}
          disabled={
            !searchTerm &&
            Object.values(advancedFilters).every((v) => v === undefined)
          }
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Clear Filters
        </Button>
      </div>

      {/* Search Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search users by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={handleSearch} disabled={loading}>
              <Search className="mr-2 h-4 w-4" />
              Search
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              {showAdvancedFilters ? "Hide" : "Show"} Advanced Filters
            </Button>
          </div>

          {showAdvancedFilters && (
            <UserAdvancedFilters
              filters={advancedFilters}
              onFiltersChange={setAdvancedFilters}
              onReset={() => {
                setAdvancedFilters({
                  role: undefined,
                  companyId: undefined,
                  country: undefined,
                  isActive: undefined,
                  approvalStatus: undefined,
                  startDate: undefined,
                  endDate: undefined,
                });
              }}
            />
          )}

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm text-muted-foreground">
                Active filters:
              </span>
              {activeFilters.map((filter, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="flex items-center gap-1 cursor-pointer"
                  onClick={() => handleRemoveFilter(filter)}
                >
                  {filter}
                  <X className="h-3 w-3" />
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search Results */}
      <Card>
        <CardHeader>
          <CardTitle>
            Search Results {users.length > 0 && `(${pagination.total} found)`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              <span>Searching users...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <p>{error}</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {searchTerm ||
                Object.values(advancedFilters).some((v) => v !== undefined)
                  ? "No users found matching your search criteria."
                  : "Enter search terms and click Search to find users."}
              </p>
            </div>
          ) : (
            <TanStackDataTable
              data={users}
              columns={columns}
              isLoading={loading}
              serverPagination={
                pagination.totalPages > 1 && pagination.total > pagination.limit
                  ? {
                      pageIndex: pagination.page,
                      pageSize: pagination.limit,
                      total: pagination.total,
                      totalPages: pagination.totalPages,
                      onPageChange: (page: number) => {
                        setPagination((prev) => ({ ...prev, page }));
                      },
                    }
                  : undefined
              }
              emptyMessage={
                searchTerm ||
                Object.values(advancedFilters).some((v) => v !== undefined)
                  ? "No users found matching your search criteria."
                  : "Enter search terms and click Search to find users."
              }
              styles={{
                headerBg: userTableConfig.styles?.headerBg,
                headerText: userTableConfig.styles?.headerText,
                rowHover: userTableConfig.styles?.rowHover,
              }}
              config={{
                enableSorting: true,
                enablePagination: false, // Server-side pagination
                enableZebraStriping: false,
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserSearch;
